import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { NotificationProvider } from "./components/provider/NotificationProvider";
import { Provider } from "react-redux";
import { store } from "./store";
import { Toaster } from "sonner";
import AuthMiddleware from "./middleware/auth.middleware";
import CustomCursor from "./components/ui/custom-cursor";
import { useEffect, Suspense, lazy } from "react";
import { initParallaxEffects, cleanupParallaxEffects } from "./utils/parallax";
import PublicMiddleware from "./middleware/public.middleware";
import { ThemeProvider } from "./components/provider/theme-provider";
import ErrorBoundary from "./components/ui/error-boundary";
import LoadingState from "./components/ui/loading-state";
import { Navigate } from "react-router-dom";


// Lazy load pages
const LoginPage = lazy(() => import("./pages/auth/login.page").then(module => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import("./pages/auth/register.page").then(module => ({ default: module.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("./pages/auth/forgot-password.page").then(module => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/auth/reset-password.page").then(module => ({ default: module.ResetPasswordPage })));
const SetRequiredPasswordPage = lazy(() => import("./pages/auth/set_required_password.page").then(module => ({ default: module.SetRequiredPasswordPage })));
const ModernInboxPage = lazy(() => import("./pages/mailbox/inbox.page").then(module => ({ default: module.ModernInboxPage })));
const StarredEmailPage = lazy(() => import("./pages/mailbox/starred.page"));
const DraftEmailPage = lazy(() => import("./pages/mailbox/draft.page"));
const NotFoundPage = lazy(() => import("./pages/404.page"));
const TrashEmailPage = lazy(() => import("./pages/mailbox/trash.page"));
const ArchivedEmailPage = lazy(() => import("./pages/mailbox/archived.page"));
const SentEmailsPage = lazy(() => import("./pages/mailbox/sent-emails.page"));
const EmailViewPage = lazy(() => import("./pages/mailbox/email-view.page"));
const ConfidentialViewPage = lazy(() => import("./pages/mailbox/confidential-view.page"));
const ScheduledEmailPage = lazy(() => import("./pages/mailbox/scheduled.page"));
const OutboxPage = lazy(() => import("./pages/mailbox/outbox.page"));
const SpamPage = lazy(() => import("./pages/mailbox/spam.page"));
const PrivacyPolicyPage = lazy(() => import("./pages/policy/privacy-policy.page"));
const TermsOfServicePage = lazy(() => import("./pages/policy/terms-of-service.page"));
const CookiePolicyPage = lazy(() => import("./pages/policy/cookie-policy.page"));
const TestPage = lazy(() => import("./pages/test.page"));
const SettingsPage = lazy(() => import("./pages/settings/settings.page"));
const AccountPage = lazy(() => import("./pages/account/account.page"));
const CalendarPage = lazy(() => import("./pages/apps/calendar.page"));
const PublicSupportPage = lazy(() => import("./pages/support/public-support.page").then(module => ({ default: module.PublicSupportPage })));
const MyTicketsPage = lazy(() => import("./pages/support/my-tickets.page").then(module => ({ default: module.MyTicketsPage })));
const TicketViewPage = lazy(() => import("./pages/support/ticket-view.page").then(module => ({ default: module.TicketViewPage })));
const ChatPage = lazy(() => import("./pages/apps/chat.page"));

const App = () => {
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      initParallaxEffects();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      cleanupParallaxEffects();
    };
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <Router>
            <NotificationProvider>
              <CustomCursor
                color="#000000"
                size={8}
                ringSize={40}
                ringColor="rgba(0, 0, 0, 0.15)"
                delay={0.08}
              />

              <Routes>
                <Route path="*" element={
                  <Suspense fallback={<LoadingState fullHeight message="Loading application..." />}>
                    <Routes>
                      {/* Public Routes */}

                      <Route path="/" element={<Navigate to="/login" replace />} />
                      {/* Mobile app OAuth callback — browser closes automatically via deep link; this page just prevents 404 */}
                      <Route path="/auth-callback" element={<LoadingState fullHeight message="Completing sign in..." />} />
                      <Route element={<PublicMiddleware />}>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                      </Route>

                      {/* Forced password change — standalone, outside both middlewares */}
                      <Route path="/set-required-password" element={<SetRequiredPasswordPage />} />

                      {/* Confidential Email View - Public (no auth required) */}
                      <Route path="/confidential/:emailId" element={<ConfidentialViewPage />} />

                      {/* Policy Pages - Public Routes */}
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                      <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                      <Route path="/contact-support" element={<PublicSupportPage />} />

                      <Route element={<AuthMiddleware />}>
                        <Route path="/inbox" element={<ModernInboxPage />} />
                        <Route path="/category/:categoryId" element={<ModernInboxPage />} />
                        <Route path="/sent" element={<SentEmailsPage />} />
                        <Route path="/starred" element={<StarredEmailPage />} />
                        <Route path="/drafts" element={<DraftEmailPage />} />
                        <Route path="/scheduled" element={<ScheduledEmailPage />} />
                        <Route path="/outbox" element={<OutboxPage />} />

                        <Route path="/archived" element={<ArchivedEmailPage />} />
                        <Route path="/trash" element={<TrashEmailPage />} />
                        <Route path="/spam" element={<SpamPage />} />

                        {/* Email View Page */}
                        <Route path="/email/:emailId" element={<EmailViewPage />} />

                        {/* Settings & Account Pages */}
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/account" element={<AccountPage />} />
                        <Route path="/calendar" element={<CalendarPage />} />

                        {/* Support Routes */}
                        <Route path="/support" element={<MyTicketsPage />} />
                        <Route path="/support/new" element={<PublicSupportPage />} />
                        <Route path="/support/:ticketId" element={<TicketViewPage />} />

                        {/* Chat App */}
                        <Route path="/chat" element={<ChatPage />} />
                      </Route>

                      <Route path="/test" element={<TestPage />} />
                      {/* 404 Catch-all Route */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                } />
              </Routes>
            </NotificationProvider>
          </Router>
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              unstyled: true,
              classNames: {
                error:
                  "bg-red-400 flex items-center rounded-md px-4 py-3 text-sm w-full mx-2",
                success:
                  "text-green-400 flex items-center rounded-md px-4 py-3 text-sm w-full mx-2",
                warning:
                  "text-yellow-400 flex items-center rounded-md px-4 py-3 text-sm w-full mx-2",
                info: "bg-blue-400 flex items-center rounded-md px-4 py-3 text-sm w-full mx-2",
              },
            }}
          />
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
